'use client';

import { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import type { ibow2_proto_PortalOfficePartnership } from '@/init/swagger';
import { getPublicPartnerships } from '@/features/partnership/api';
import { PartnerBadge } from '../PartnerBadge';

const MAX_VISIBLE_NAMES = 3;

type Props = {
  portalOfficeId: number;
};

export function PartnershipsSummary({ portalOfficeId }: Props) {
  const [partnerships, setPartnerships] = useState<
    ibow2_proto_PortalOfficePartnership[]
  >([]);

  useEffect(() => {
    if (!portalOfficeId) return;

    const promise = getPublicPartnerships(portalOfficeId);
    promise
      .then((res) => {
        setPartnerships(res.partnerships ?? []);
      })
      .catch(() => {
        setPartnerships([]);
      });

    return () => {
      promise.cancel();
    };
  }, [portalOfficeId]);

  if (partnerships.length === 0) {
    return null;
  }

  const visibleNames = partnerships
    .slice(0, MAX_VISIBLE_NAMES)
    .map((p) => p.partner_portal_office?.name ?? '')
    .filter(Boolean);

  const remaining = partnerships.length - MAX_VISIBLE_NAMES;

  const nameText = [
    visibleNames.join('・'),
    remaining > 0 ? `他${remaining}件` : '',
  ]
    .filter(Boolean)
    .join('・');

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1,
      }}
    >
      <PartnerBadge />
      <Typography
        variant="JP/Body/s/Regular/Normal"
        color="text.secondary"
        sx={{ alignSelf: 'center' }}
      >
        {nameText}
      </Typography>
    </Box>
  );
}
